import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X, Sparkles, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import SpeakButton from './SpeakButton';
import DictateButton from './DictateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PetCoach({ pet }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const scrollRef = useRef(null);

  const speakText = (text) => {
    try {
      if (!('speechSynthesis' in window) || !text) return;
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(text);
      u.lang = 'sv-SE';
      u.rate = 1.0;
      u.pitch = 1.1;
      window.speechSynthesis.speak(u);
    } catch { /* browser SpeechSynthesis unavailable */ }
  };

  const toggleVoiceMode = () => {
    setVoiceMode((v) => {
      const next = !v;
      if (!next) {
        try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
      }
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/coach/history?limit=20`, { withCredentials: true });
        setMessages(data.items || []);
      } catch { /* noop */ }
    })();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/coach/chat`,
        { message: msg, session_id: sessionId },
        { withCredentials: true }
      );
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
      // Auto-read the reply aloud when voice mode is on
      if (voiceMode && data.reply) {
        setTimeout(() => speakText(data.reply), 150);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte nå coachen');
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const petName = pet?.name || 'Ditt husdjur';

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 neo-btn w-14 h-14 rounded-full bg-gradient-to-br from-[#FFC8DD] to-[#C8B6FF] flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:scale-110 transition-transform"
        title="Pratstund med ditt husdjur"
        data-testid="coach-fab"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="coach-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative neo-card bg-white dark:bg-[#252545] w-full sm:max-w-lg h-[85vh] sm:h-[600px] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b-2 border-black bg-gradient-to-r from-[#FFE8F0] to-[#E8F0FF]">
              <div className="flex items-center gap-2">
                {pet?.image && (
                  <div className="w-10 h-10 rounded-full border-2 border-black overflow-hidden">
                    <img src={pet.image} alt={petName} className="w-full h-full object-cover" />
                  </div>
                )}
                <div>
                  <p className="font-bold font-['Fredoka'] text-sm">Pratstund med {petName}</p>
                  <p className="text-[11px] opacity-60">Din ADHD-vänliga kompis 💛</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleVoiceMode}
                  className={`neo-btn p-1.5 flex items-center gap-1 text-[10px] font-bold ${voiceMode ? 'bg-[#B9FBC0]' : 'bg-white'}`}
                  title={voiceMode ? 'Röstläge: PÅ (husdjuret läser upp svar)' : 'Röstläge: AV'}
                  data-testid="coach-voice-mode"
                >
                  {voiceMode ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">{voiceMode ? 'Röst på' : 'Röst av'}</span>
                </button>
                <button onClick={() => setOpen(false)} className="neo-btn p-1.5 bg-white" data-testid="coach-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="coach-messages">
              {messages.length === 0 && !sending && (
                <div className="text-center py-8 opacity-70">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Säg hej till {petName}</p>
                  <p className="text-[11px] mt-2">T.ex. "Jag känner mig överväldigad idag" eller "Vad ska jag börja med?"</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} gap-1 items-end`}>
                  <div
                    className={`max-w-[85%] rounded-2xl border-2 border-black px-3 py-2 text-sm ${
                      m.role === 'user' ? 'bg-[#C8B6FF] rounded-br-sm' : 'bg-[#FFE8F0] rounded-bl-sm'
                    }`}
                    data-testid={`coach-msg-${m.role}`}
                  >
                    <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                  </div>
                  {m.role === 'assistant' && (
                    <SpeakButton text={m.content} label="" size="xs" />
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl border-2 border-black px-3 py-2 bg-[#FFE8F0] rounded-bl-sm">
                    <p className="text-sm italic opacity-70 animate-pulse">…tänker</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t-2 border-black bg-white flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Skriv eller diktera…"
                className="flex-1 px-3 py-2 rounded-xl border-2 border-black text-sm resize-none"
                data-testid="coach-input"
              />
              <DictateButton
                onAppend={(said) => setInput((prev) => (prev ? prev + ' ' : '') + said)}
                size="sm"
              />
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                className="neo-btn p-2 bg-[#C8B6FF] disabled:opacity-50"
                data-testid="coach-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
